import { Component, inject, OnInit } from '@angular/core';
import { filter, first, Observable } from 'rxjs';
import {
    GrdfAccessRightModel,
    GrdfAccessRightStatus,
    GrdfRemoteAccessRightModel,
} from '../../grdf.models';
import { Store } from '@ngxs/store';
import {
    CreateAccessRight,
    DeleteAccessRight,
    GrdfState,
    GetAllGrdfAccessRights,
    UnsetCurrentAccessRightId,
    DeleteAccessRemoteRight,
} from '../../grdf.state';
import {
    DisplayedColumns,
    DisplayType,
} from 'src/app/shared/components/ed-table/ed-table.component';
import {
    AlertService,
    DropdownMenuItem,
    LayerService,
    LayerSize,
    PopupService,
    PopupTypes,
} from '@ed---interne/ng-uui-components';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatTableDataSource } from '@angular/material/table';
import { User } from 'src/app/core/models/user.model';
import { AuthState } from 'src/app/core/states/auth.state';
import { GrdfAccessRightCreateComponent } from './access-right-create/grdf-access-right-create.component';
import { LayerPopupOptions } from 'src/app/shared/all.constants';
import { Router } from '@angular/router';

enum MeterMenuEvent {
    Access = 'Access',
    Delete = 'Delete',
    Restore = 'Restore',
}

const GrdfAccessRightStatusErrorList = [
    GrdfAccessRightStatus.CHECK_AGAIN,
    GrdfAccessRightStatus.REFUSED,
    GrdfAccessRightStatus.UNKNOW,
];

interface AccessRightTableElement extends Partial<GrdfAccessRightModel> {
    actions: DropdownMenuItem[];
}

@UntilDestroy()
@Component({
    selector: 'app-grdf-dashboard',
    templateUrl: './grdf-dashboard.component.html',
    styleUrls: ['./grdf-dashboard.component.scss'],
})
export class GrdfComponent implements OnInit {
    public grdfAccessRights$: Observable<GrdfAccessRightModel[]> = inject(Store)
        .select(GrdfState.grdfAccessRights)
        .pipe(filter(Boolean));

    public grdfRemoteAccessRights$: Observable<GrdfRemoteAccessRightModel[]> = inject(Store)
        .select(GrdfState.grdfRemoteAccessRights)
        .pipe(filter(Boolean));
    public grdfRemoteAccessRightsDatasource: MatTableDataSource<AccessRightTableElement> =
        new MatTableDataSource<AccessRightTableElement>();
    public grdfRemoteAccessRightsColumns: DisplayedColumns[] = [
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'PDL',
            objectKey: 'id_pce',
            bold: true,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Nom ou raison sociale',
            objectKey: 'nom_ou_raison_sociale_du_titulaire',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Email',
            objectKey: 'courriel_titulaire',
            bold: false,
        },
        {
            displayType: DisplayType.DATE,
            objectDisplayName: "Début du droit d'accès",
            objectKey: 'date_debut_droit_acces',
            bold: false,
        },
        {
            displayType: DisplayType.DATE,
            objectDisplayName: "Fin du droit d'accès",
            objectKey: 'date_fin_droit_acces',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Statut',
            objectKey: 'etat_droit_acces',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'détail',
            objectKey: 'errorMessage',
            bold: false,
        },
    ];

    public isLoading = false;
    public isLoading$: Observable<boolean> = inject(Store).select(GrdfState.isLoading);

    loggedUser$: Observable<User | null> = inject(Store).select(AuthState.loggedUser);
    loggedUser: User | undefined;

    public meterCounter = 0;
    public meterActiveCounter = 0;
    public meterPendingCounter = 0;
    public meterDisabledCounter = 0;
    public meterErrorCounter = 0;
    public meterExpiredCounter = 0;
    public meterRevokedCounter = 0;

    public meterActiveDataSource: MatTableDataSource<AccessRightTableElement> =
        new MatTableDataSource<AccessRightTableElement>();
    public meterPendingDataSource: MatTableDataSource<AccessRightTableElement> =
        new MatTableDataSource<AccessRightTableElement>();
    public meterExpiredDataSource: MatTableDataSource<AccessRightTableElement> =
        new MatTableDataSource<AccessRightTableElement>();
    public meterDisabledDataSource: MatTableDataSource<AccessRightTableElement> =
        new MatTableDataSource<AccessRightTableElement>();
    public meterErrorDataSource: MatTableDataSource<AccessRightTableElement> =
        new MatTableDataSource<AccessRightTableElement>();
    public meterRevokedDataSource: MatTableDataSource<AccessRightTableElement> =
        new MatTableDataSource<AccessRightTableElement>();

    public meterActiveDisplayedColumns: DisplayedColumns[] = [
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'PDL',
            objectKey: 'pdlNumber',
            bold: true,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Nom ou Raison sociale',
            objectKey: 'clientName',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'SIRET',
            objectKey: 'clientSirenSiret',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Email',
            objectKey: 'clientRepresentativeEmail',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Téléphone',
            objectKey: 'clientRepresentativePhoneNumber',
            bold: false,
        },
        {
            displayType: DisplayType.DATE,
            objectDisplayName: 'Création',
            objectKey: 'consentStart',
            bold: false,
        },
        {
            displayType: DisplayType.DATE,
            objectDisplayName: 'Validité',
            objectKey: 'consentEnd',
            bold: false,
        },
    ];

    public meterErrorDisplayedColumns: DisplayedColumns[] = [
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'PDL',
            objectKey: 'pdlNumber',
            bold: true,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Nom ou Raison sociale',
            objectKey: 'clientName',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'SIRET',
            objectKey: 'clientSirenSiret',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Email',
            objectKey: 'clientRepresentativeEmail',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Téléphone',
            objectKey: 'clientRepresentativePhoneNumber',
            bold: false,
        },
        {
            displayType: DisplayType.DATE,
            objectDisplayName: 'Création',
            objectKey: 'consentStart',
            bold: false,
        },
        {
            displayType: DisplayType.DATE,
            objectDisplayName: 'Erreur',
            objectKey: 'errorAt',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Message',
            objectKey: 'errorMessage',
            bold: false,
        },
    ];

    public meterDisabledDisplayedColumns: DisplayedColumns[] = [
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'PDL',
            objectKey: 'pdlNumber',
            bold: true,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Nom ou Raison sociale',
            objectKey: 'clientName',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'SIRET',
            objectKey: 'clientSirenSiret',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Email',
            objectKey: 'clientRepresentativeEmail',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Téléphone',
            objectKey: 'clientRepresentativePhoneNumber',
            bold: false,
        },
        {
            displayType: DisplayType.DATE,
            objectDisplayName: 'Date de révocation',
            objectKey: 'disabledAt',
            bold: false,
        },
    ];

    constructor(
        private readonly store: Store,
        private router: Router,
        private readonly popupService: PopupService,
        private readonly alertService: AlertService,
        private readonly layerService: LayerService,
    ) {}

    public ngOnInit(): void {
        this.store.dispatch([new UnsetCurrentAccessRightId(), new GetAllGrdfAccessRights()]);
        this.grdfRemoteAccessRights$
            .pipe(untilDestroyed(this))
            .subscribe((grdfRemoteAccessRights: any) => {
                this.grdfRemoteAccessRightsDatasource =
                    new MatTableDataSource<AccessRightTableElement>(
                        grdfRemoteAccessRights.map((grar: GrdfRemoteAccessRightModel) => ({
                            ...grar,
                            nom_ou_raison_sociale_du_titulaire:
                                grar.nom_titulaire || grar.raison_sociale_du_titulaire,
                            actions: this.getRemoteActions(),
                        })),
                    );
            });

        this.loggedUser$.pipe(untilDestroyed(this)).subscribe((data: any) => {
            this.loggedUser = data;
            if (this.loggedUser?.isAdmin) {
                const orgColumn = {
                    displayType: DisplayType.TEXT,
                    objectDisplayName: 'Organisation',
                    objectKey: 'organizationName',
                    bold: false,
                };
                this.meterActiveDisplayedColumns.unshift(orgColumn);
                this.meterDisabledDisplayedColumns.unshift(orgColumn);
                this.meterErrorDisplayedColumns.unshift(orgColumn);
            }
        });

        this.isLoading$!.pipe(untilDestroyed(this)).subscribe((loadingState) => {
            this.isLoading = loadingState;
        });

        this.grdfAccessRights$
            .pipe(untilDestroyed(this))
            .subscribe((grdfAccessRights: GrdfAccessRightModel[]) => {
                //Merge clientName and clientRepresentativeName as on is for professionnal and other for individuals
                grdfAccessRights = grdfAccessRights.map((gar) => ({
                    ...gar,
                    clientName: gar.clientName || gar.clientRepresentativeName,
                    organizationName: gar.organization?.name,
                }));

                this.meterActiveDataSource = new MatTableDataSource<AccessRightTableElement>(
                    grdfAccessRights
                        .filter((ar) => ar.status === GrdfAccessRightStatus.ACTIVE)
                        .map((ar) => ({
                            ...ar,
                            actions: this.getActions(),
                        })),
                );

                this.meterPendingDataSource = new MatTableDataSource<AccessRightTableElement>(
                    grdfAccessRights
                        .filter((ar) => ar.status === GrdfAccessRightStatus.PENDING_VALIDATION)
                        .map((ar) => ({
                            ...ar,
                            actions: this.getPendingActions(),
                        })),
                );

                this.meterErrorDataSource = new MatTableDataSource<AccessRightTableElement>(
                    grdfAccessRights
                        .filter((ar) => GrdfAccessRightStatusErrorList.includes(ar.status))
                        .map((ar) => ({
                            ...ar,
                            errorMessage: ar.errorMessage || ar.status,
                            actions: [],
                        })),
                );

                this.meterRevokedDataSource = new MatTableDataSource<AccessRightTableElement>(
                    grdfAccessRights
                        .filter((ar) => ar.status === GrdfAccessRightStatus.REVOKED)
                        .map((ar) => ({
                            ...ar,
                            organizationName: ar.organization.name,
                            disabledAt: ar.localRevokedAt,
                            actions: [],
                        })),
                );

                this.meterExpiredDataSource = new MatTableDataSource<AccessRightTableElement>(
                    grdfAccessRights
                        .filter((ar) => ar.status === GrdfAccessRightStatus.OBSOLETE)
                        .map((ar) => ({
                            ...ar,
                            actions: [],
                        })),
                );

                this.meterDisabledDataSource = new MatTableDataSource<AccessRightTableElement>(
                    grdfAccessRights
                        .filter((ar) => ar.status === GrdfAccessRightStatus.DISABLED)
                        .map((ar) => ({
                            ...ar,
                            actions: [
                                {
                                    icon: 'icon-flip-backward',
                                    text: 'Restaurer',
                                    outputEventString: MeterMenuEvent.Restore,
                                },
                            ],
                        })),
                );

                this.meterCounter = grdfAccessRights.length;
                this.meterActiveCounter = grdfAccessRights.filter(
                    (ar) => ar.status === GrdfAccessRightStatus.ACTIVE,
                ).length;
                this.meterPendingCounter = grdfAccessRights.filter(
                    (ar) => ar.status === GrdfAccessRightStatus.PENDING_VALIDATION,
                ).length;
                this.meterDisabledCounter = grdfAccessRights.filter(
                    (ar) => ar.status === GrdfAccessRightStatus.DISABLED,
                ).length;
                this.meterErrorCounter = grdfAccessRights.filter((ar) =>
                    GrdfAccessRightStatusErrorList.includes(ar.status),
                ).length;
                this.meterExpiredCounter = grdfAccessRights.filter(
                    (ar) => ar.status === GrdfAccessRightStatus.OBSOLETE,
                ).length;
                this.meterRevokedCounter = grdfAccessRights.filter(
                    (ar) => ar.status === GrdfAccessRightStatus.REVOKED,
                ).length;
            });
    }

    private getRemoteActions(): DropdownMenuItem[] {
        const actions: DropdownMenuItem[] = [
            {
                icon: 'icon-eye',
                text: "Importer l'accès",
                outputEventString: MeterMenuEvent.Access,
            },
            {
                icon: 'icon-trash-01',
                text: 'Révoquer',
                outputEventString: MeterMenuEvent.Delete,
            },
        ];

        return actions;
    }

    private getActions(): DropdownMenuItem[] {
        const actions: DropdownMenuItem[] = [
            {
                icon: 'icon-eye',
                text: 'Accéder',
                outputEventString: MeterMenuEvent.Access,
            },
            {
                icon: 'icon-trash-01',
                text: 'Révoquer',
                outputEventString: MeterMenuEvent.Delete,
            },
        ];

        return actions;
    }
    private getPendingActions(): DropdownMenuItem[] {
        const actions: DropdownMenuItem[] = [
            {
                icon: 'icon-trash-01',
                text: 'Révoquer',
                outputEventString: MeterMenuEvent.Delete,
            },
        ];

        return actions;
    }

    public deleteAccessRight(accessRight: GrdfAccessRightModel): void {
        this.popupService
            .openPopup({
                title: "Révocation de l'accès",
                htmlContent: `Confirmez-vous la révocation de l'accès au PRM <b>${accessRight.pdlNumber}</b> ?`,
                confirmButtonText: 'Révoquer',
                type: PopupTypes.Delete,
            })
            .then((result) => {
                if (result.isConfirmed) {
                    this.store
                        .dispatch(new DeleteAccessRight({ accessRightId: accessRight?.id }))
                        .pipe(untilDestroyed(this))
                        .subscribe({
                            next: () => {
                                this.alertService.valid('Révocation', "L'accès a bien été révoqué");
                            },
                            error: (err) => {
                                this.alertService.error(
                                    'Révocation',
                                    "une erreur est survenue : Impossible de révoquer l'accès",
                                );
                            },
                        });
                }
            });
    }

    public unDeleteAccessRight(accessRight: GrdfAccessRightModel): void {
        if (new Date(accessRight.consentEnd).getTime() < new Date().getTime()) {
            this.alertService.error(
                'Réstauration',
                'Vous ne pouvez pas restaurer un accès dont la date de péremption est dépassée.',
            );
            return;
        }

        this.popupService
            .openPopup({
                title: "Récupération de l'accès",
                htmlContent: `Confirmez-vous la récupération de l'accès au PRM <b>${accessRight.pdlNumber}</b> ?`,
                confirmButtonText: 'Récupérer',
                type: PopupTypes.Info,
            })
            .then((result) => {
                if (result.isConfirmed) {
                    this.store
                        .dispatch(new CreateAccessRight({ accessRight }))
                        .pipe(untilDestroyed(this))
                        .subscribe({
                            next: () => {
                                this.alertService.valid(
                                    'Réstauration',
                                    "L'accès a bien été restauré",
                                );
                            },
                            error: (err) => {
                                this.alertService.error(
                                    'Réstauration',
                                    "une erreur est survenue : Impossible de restaurer l'accès",
                                );
                            },
                        });
                }
            });
    }

    public onDropdownMenuClick(grdfAccessRight: GrdfAccessRightModel, event: string): void {
        switch (event) {
            case MeterMenuEvent.Access:
                this.goToDetail(grdfAccessRight.id);
                break;
            case MeterMenuEvent.Delete:
                return this.deleteAccessRight(grdfAccessRight);
            case MeterMenuEvent.Restore:
                return this.unDeleteAccessRight(grdfAccessRight);
            default:
                break;
        }
    }

    public createAccessRightFromRemote(remote: GrdfRemoteAccessRightModel) {
        if (remote.etat_droit_acces !== GrdfAccessRightStatus.ACTIVE)
            return this.alertService.error(
                'Création',
                "Seuls les droits d'accès actifs peuvent être importés",
            );

        const accessRightToCreate = {
            pdlNumber: remote.id_pce,
            clientRepresentativeEmail: remote.courriel_titulaire,
            clientRepresentativeName: remote.nom_titulaire,
            clientRepresentativePhoneNumber: remote.numero_telephone_mobile_titulaire,
            clientName: remote.raison_sociale_du_titulaire,
            clientSirenSiret: remote.raison_sociale_du_titulaire, //Hack
            consentEnd: remote.date_fin_droit_acces,
            consentStart: remote.date_debut_droit_acces,
            consumptionAccessStart: remote.date_debut_droit_acces,
            pdlPostalCode: remote.code_postal,
            validationDate: remote.date_debut_autorisation,
        };
        this.store
            .dispatch(
                new CreateAccessRight({
                    accessRight: accessRightToCreate,
                }),
            )
            .subscribe({
                next: () => {
                    this.alertService.valid("Nouveau droit d'accès", "Droit d'accès créé !");
                },
                error: (err) => {
                    console.error(err);
                    console.info(err?.message ?? err?.error?.message);
                    this.alertService.error(
                        'Création',
                        err?.message ?? err?.error?.message ?? 'Une erreur est survenue',
                    );
                },
            });
    }

    public onDropdownMenuClickRemote(
        remotegrdfAccessRight: GrdfRemoteAccessRightModel,
        event: string,
    ): void {
        const isDeleteDisabled =
            event === MeterMenuEvent.Delete &&
            remotegrdfAccessRight.etat_droit_acces === 'Révoquée';

        if (isDeleteDisabled) {
            this.alertService.error('Révocation', 'Droit déjà révoqué !');
            return;
        }

        switch (event) {
            case MeterMenuEvent.Access:
                this.createAccessRightFromRemote(remotegrdfAccessRight);
                break;
            case MeterMenuEvent.Delete:
                this.deleteAccessRightRemote(remotegrdfAccessRight);
                break;
            default:
                break;
        }
    }

    public deleteAccessRightRemote(remote: GrdfRemoteAccessRightModel): void {
        this.popupService
            .openPopup({
                title: "Révocation de l'accès",
                htmlContent: `Confirmez-vous la révocation de l'accès au PRM <b>${remote.id_pce}</b> Cette action est définitive ?`,
                confirmButtonText: 'Révoquer',
                type: PopupTypes.Delete,
            })
            .then((result) => {
                if (result.isConfirmed) {
                    this.store
                        .dispatch(
                            new DeleteAccessRemoteRight({ accessRightId: remote?.id_droit_acces }),
                        )
                        .pipe(untilDestroyed(this))
                        .subscribe({
                            next: () => {
                                this.alertService.valid('Révocation', "L'accès a bien été révoqué");
                            },
                            error: (err) => {
                                this.alertService.error(
                                    'Révocation',
                                    "une erreur est survenue : Impossible de révoquer l'accès",
                                );
                            },
                        });
                }
            });
    }

    public async goToDetail(accessRightId: string): Promise<void> {
        this.router.navigate(['/main/grdf/' + accessRightId + '/informations']);
    }

    public openModalCreateAccessRight(): void {
        const header = "Déclarer un nouveau droit d'accès";
        this.layerService.show(
            GrdfAccessRightCreateComponent,
            { header },
            { size: LayerSize.Large, withClosePopup: LayerPopupOptions.Creating },
        );
    }
}
